// @MX:ANCHOR: [AUTO] businessLicenseOcrMixin — 영업집조 업로드 + OCR 자동채움 공유 진입점 (fan_in >= 2)
// @MX:REASON: [AUTO] CompanyFormModal.vue(운영자 병원·기공소 등록/수정) + CompanyProfile.vue(병원·기공소
//             자기 프로필 수정) 양쪽에서 재사용. 이 로직이 두 벌이 되면 "제안 규칙이 화면마다 다른"
//             상태가 되고, 그 드리프트는 이 리포에서 이미 /info와 /hospital-profile 사이에 실제로
//             발생했다(같은 PATCH를 서로 다른 필드 집합으로 호출). 자동채움 규칙의 단일 출처.
// @MX:SPEC: SPEC-CHINA-BIZLICENSE-OCR-001

import {
  uploadBusinessLicenseImage,
  extractBusinessLicenseOcr,
} from '@/apis/chinaCompany';

/**
 * 영업집조(营业执照) 업로드 + OCR 자동채움 공유 mixin.
 *
 * 호스트 컴포넌트가 제공해야 하는 것 (contract):
 *   - data.formData : 최소한 businessLicenseImageUrl 키를 가진 객체.
 *                     OCR 적용 대상 키(name/ceo/cNo/addrOne/addrTwo/addrThree/addrDetail)도 여기에 있어야
 *                     applyOcrSelection이 값을 넣을 수 있다.
 *   - computed.isEdit : 제거 버튼 노출 규칙에 쓰인다(아래 showBusinessLicenseRemove 주석 참조).
 *                       자기 프로필처럼 항상 수정인 화면은 true를 반환하면 된다.
 *   - (선택) computed.ocrFieldLabels : 제안 패널의 필드 라벨. 화면마다 회사명 라벨이 다르면
 *                       (예: 모달은 병원/기공소 접미사) 오버라이드한다. 미오버라이드 시 기본값 사용.
 *
 * 이 mixin이 소유하는 것:
 *   - 업로드 위젯 상태(uploading/error/persisted URL)와 사전 검증·에러 매핑
 *   - OCR 실행/재시도/제안/선택 적용/provenance 상태 전부
 *
 * 이 mixin이 소유하지 않는 것:
 *   - 저장(payload 구성) — 화면마다 엔드포인트가 다르다(운영자 /china/*, 자기 프로필 /companies/:id)
 *   - 제안 패널·업로드 위젯의 마크업 — 템플릿은 각 화면이 그린다(폼 레이아웃이 다르다)
 */
export const businessLicenseOcrMixin = {
  data() {
    return {
      // formData.businessLicenseImageUrl과 별도로 "서버에 저장된 값"을 보관한다.
      // 호스트가 폼 초기화 시점에 채워야 한다(수정 대상의 기존 이미지 URL).
      persistedBusinessLicenseImageUrl: '',
      businessLicenseUploading: false,
      businessLicenseError: '',
      // 영업집조 OCR 자동 채움 — SPEC-CHINA-BIZLICENSE-OCR-001 M3
      ocrExtracting: false,
      ocrAttempted: false, // 이번 업로드 세션에서 자동 실행이 최소 1회 시도됐는지 — 재시도 링크 노출 판정용
      ocrError: '',
      ocrProposal: null, // { provider, fields: {key:value}, warnings: string[] } | null
      ocrSelected: {}, // { [fieldKey]: boolean } — 제안 패널의 체크박스 상태
      // 실제로 폼에 적용된(사람이 동의한) OCR 유래 필드 키 목록 — provenance 칩 노출용
      ocrProvenanceFields: [],
    };
  },

  computed: {
    // 현재 값이 서버 저장값과 다른지 — "이번 세션 신규 업로드" 판별용
    isBusinessLicenseModified() {
      return (
        this.formData.businessLicenseImageUrl !== this.persistedBusinessLicenseImageUrl
      );
    },
    // 판단 18/B-7: create는 값 있으면 항상 노출. edit은 신규 업로드(미저장 변경)일 때만 노출.
    // edit + 저장된 원본 그대로일 땐 "제거"를 렌더링하지 않는다 — 스토리지 오브젝트는 콘솔 삭제
    // 전까진 공개 상태로 남으므로(판단 12), 필드 제거가 접근 차단을 의미한다는 착시를 주지 않기 위함.
    showBusinessLicenseRemove() {
      if (!this.formData.businessLicenseImageUrl) return false;
      if (!this.isEdit) return true;
      return this.isBusinessLicenseModified;
    },
    /**
     * 제안 패널 필드 라벨 기본값 — 기존 폼 필드 번역키를 그대로 재사용(번역 중복 방지).
     * 회사명 라벨이 화면마다 다르면 호스트가 같은 이름의 computed로 오버라이드한다
     * (Vue mixin 병합 규칙: 컴포넌트 정의가 mixin을 덮는다).
     */
    ocrFieldLabels() {
      return {
        name: this.$t('company_form_name'),
        ceo: this.$t('company_form_ceo'),
        cNo: this.$t('company_form_cNo'),
        addrOne: this.$t('company_form_addrOne'),
        addrTwo: this.$t('company_form_addrTwo'),
        addrThree: this.$t('company_form_addrThree'),
        addrDetail: this.$t('company_form_addrDetail'),
      };
    },
    // OCR 제안 패널 렌더용 — 응답 fields 객체를 라벨 붙은 배열로 변환
    ocrProposalFieldList() {
      if (!this.ocrProposal) return [];
      const labels = this.ocrFieldLabels;
      return Object.keys(this.ocrProposal.fields).map((key) => ({
        key,
        label: labels[key] || key,
        value: this.ocrProposal.fields[key],
      }));
    },
    hasOcrSelection() {
      return Object.values(this.ocrSelected).some(Boolean);
    },
  },

  methods: {
    // 영업집조 이미지 클라이언트 사전 검증 (before-upload) — UX용, 서버가 최종 권위(REQ-CS-042)
    beforeUploadBusinessLicense(file) {
      const MAX_SIZE = 15 * 1024 * 1024;
      if (file.size > MAX_SIZE) {
        this.businessLicenseError = this.$t('company_form_businessLicense_error_size');
        return false;
      }
      const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
      const ALLOWED_EXT = /\.(jpe?g|png|webp)$/i;
      if (!ALLOWED_TYPES.includes(file.type) && !ALLOWED_EXT.test(file.name)) {
        this.businessLicenseError = this.$t('company_form_businessLicense_error_format');
        return false;
      }
      this.businessLicenseError = '';
      return true;
    },

    /**
     * 영업집조 이미지 업로드 (custom-request) — REQ-CS-040~044
     * 성공 시에만 formData.businessLicenseImageUrl 갱신. 실패는 위젯 범위 에러로만 표시하고
     * formData는 절대 변경하지 않는다(값 불변, 상태 (d)).
     */
    async onUploadBusinessLicense({ file }) {
      this.businessLicenseUploading = true;
      this.businessLicenseError = '';
      try {
        const res = await uploadBusinessLicenseImage(file);
        const url = res?.data?.url;
        if (url) {
          this.formData.businessLicenseImageUrl = url;
          // 새 이미지가 들어오면 이전 이미지에 대한 OCR 제안/provenance는 전부 무효 — 먼저 비운다
          this.resetOcrState();
          // SPEC 판단: 자동·즉시 실행(버튼 없음). 업로드 자체의 성공/완료를 막지 않도록 await하지 않는다.
          this.runBusinessLicenseOcr(url);
        } else {
          // 201인데 url이 없는 예외적 응답 shape — 안전하게 실패로 취급
          this.businessLicenseError = this.$t('company_form_businessLicense_error_generic');
        }
      } catch (err) {
        this.businessLicenseError = this.mapBusinessLicenseUploadError(err);
      } finally {
        this.businessLicenseUploading = false;
      }
    },

    /** BE 에러 토큰 → 중문 메시지 + 원문 병기 (REQ-CS-041) */
    mapBusinessLicenseUploadError(err) {
      const status = err?.response?.status;
      const rawMessage = err?.response?.data?.message || '';

      if (!err?.response) {
        // 네트워크 단절 또는 클라이언트 타임아웃(65s)
        return this.$t('company_form_businessLicense_error_network');
      }
      if (status === 400) {
        if (rawMessage.includes('CS-FILE-001')) {
          return `${this.$t('company_form_businessLicense_error_format')} — ${rawMessage}`;
        }
        if (rawMessage.includes('CS-FILE-002')) {
          return `${this.$t('company_form_businessLicense_error_size')} — ${rawMessage}`;
        }
        if (rawMessage.includes('CS-FILE-003')) {
          return `${this.$t('company_form_businessLicense_error_dimensions')} — ${rawMessage}`;
        }
        return rawMessage
          ? `${this.$t('company_form_businessLicense_error_generic')} — ${rawMessage}`
          : this.$t('company_form_businessLicense_error_generic');
      }
      if (status === 403) {
        // BE denyMessage(CS-AUTHZ-001)는 한국어다(SPEC §4 리터럴). 중국 운영자가 주 사용자이므로
        // 원문만 노출하면 읽을 수 없다 — 다른 토큰과 동일하게 "중문 + 원문 병기"로 통일한다.
        return rawMessage
          ? `${this.$t('company_form_businessLicense_error_forbidden')} — ${rawMessage}`
          : this.$t('company_form_businessLicense_error_forbidden');
      }
      if (status === 429) {
        return this.$t('company_form_businessLicense_error_throttled');
      }
      if (status === 502) {
        return `${this.$t('company_form_businessLicense_error_upload_failed')} — ${rawMessage}`;
      }
      return rawMessage
        ? `${this.$t('company_form_businessLicense_error_generic')} — ${rawMessage}`
        : this.$t('company_form_businessLicense_error_generic');
    },

    /**
     * 제거 버튼 — 판단 18/C-2
     * - create 모드: 빈 문자열로 설정 → 저장 payload의 빈값 필터에 걸려 실제로 미전송(진짜 빈 값)
     * - edit 모드(신규 업로드 상태): 저장된 원본으로 복원(교체 취소) — ''로 설정하면 화면에 따라
     *   빈값 필터에 걸려 필드가 빠지고 서버가 기존 값을 유지 → "빈 미리보기 후 저장하면 옛 이미지
     *   부활"이라는 금지된 착시가 발생하므로 반드시 persisted 값으로 되돌린다.
     * - edit 모드 + 저장된 원본 그대로: showBusinessLicenseRemove가 false라 버튼 자체가 렌더링 안 됨.
     */
    onRemoveBusinessLicense() {
      if (!this.isEdit) {
        this.formData.businessLicenseImageUrl = '';
      } else {
        this.formData.businessLicenseImageUrl = this.persistedBusinessLicenseImageUrl;
      }
      // 이미지 자체가 사라지므로(또는 교체가 취소되므로) 그 이미지에 대한 OCR 제안도 함께 무효화
      this.resetOcrState();
    },

    /**
     * 영업집조 OCR 자동 채움 상태 초기화 — SPEC-CHINA-BIZLICENSE-OCR-001 M3.
     * 새 업로드/제거/화면 재진입 시 이전 이미지에 결부된 제안·에러·provenance를 전부 비운다.
     * ocrAttempted도 함께 리셋한다 — 이 플래그는 "이번 이미지에 자동 실행이 있었는가"만 뜻하며
     * 실행 링크의 라벨(이미지 인식 / 다시 인식)을 가르는 용도다. 링크 노출 자체를 이 값으로
     * 막으면 안 된다 — 이미 저장된 영업집조는 화면 재진입 시 항상 false라 인식을 못 돌리게 된다.
     */
    resetOcrState() {
      this.ocrExtracting = false;
      this.ocrAttempted = false;
      this.ocrError = '';
      this.ocrProposal = null;
      this.ocrSelected = {};
      this.ocrProvenanceFields = [];
    },

    /**
     * 영업집조 OCR 실행 — 업로드 성공 직후 자동 1회, 이후엔 재시도 링크로 수동 재실행.
     * provider는 절대 지정하지 않는다(서버 기본값 tencent를 그대로 사용, REQ: 프로바이더 선택 UI 없음).
     * 실패해도 폼 본체는 전혀 건드리지 않는다 — 위젯 범위 경고(ocrError)로만 표시.
     */
    async runBusinessLicenseOcr(url) {
      this.ocrExtracting = true;
      this.ocrAttempted = true;
      this.ocrError = '';
      try {
        const res = await extractBusinessLicenseOcr(url);
        const fields = res?.data?.fields || {};
        const warnings = res?.data?.warnings || [];
        this.ocrProposal = { provider: res?.data?.provider, fields, warnings };
        // 프리셀렉트 — 폼 필드가 비어 있을 때만 기본 선택. 이미 값이 있는 필드는
        // 사람이 명시적으로 체크하기 전까지 선택하지 않는다(REQ: 기존 입력값 보호).
        const selected = {};
        Object.keys(fields).forEach((key) => {
          const current = this.formData[key];
          selected[key] = current === '' || current === undefined || current === null;
        });
        this.ocrSelected = selected;
      } catch (err) {
        this.ocrProposal = null;
        this.ocrSelected = {};
        this.ocrError = this.mapOcrError(err);
      } finally {
        this.ocrExtracting = false;
      }
    },

    /** 재시도 링크 — 자동 실행 실패 시 또는 결과가 마음에 들지 않을 때 사람이 다시 시도 */
    retryBusinessLicenseOcr() {
      if (this.ocrExtracting || !this.formData.businessLicenseImageUrl) return;
      this.ocrProposal = null;
      this.ocrSelected = {};
      this.runBusinessLicenseOcr(this.formData.businessLicenseImageUrl);
    },

    /** 선택된 필드만 formData에 반영 — 체크 안 된 필드(주로 이미 값이 있던 필드)는 그대로 둔다 */
    applyOcrSelection() {
      if (!this.ocrProposal) return;
      const appliedKeys = [];
      Object.keys(this.ocrSelected).forEach((key) => {
        if (this.ocrSelected[key] && this.ocrProposal.fields[key] !== undefined) {
          this.formData[key] = this.ocrProposal.fields[key];
          appliedKeys.push(key);
        }
      });
      if (appliedKeys.length) {
        // 이전에 적용된 필드와 병합(중복 제거) — 여러 차례 재시도+부분 적용을 반복해도 누적된다
        this.ocrProvenanceFields = Array.from(
          new Set([...this.ocrProvenanceFields, ...appliedKeys]),
        );
      }
      this.dismissOcrProposal();
    },

    /** 제안 패널 닫기 — 적용 여부와 무관하게 패널만 접는다(값은 이미 적용된 대로 유지) */
    dismissOcrProposal() {
      this.ocrProposal = null;
      this.ocrSelected = {};
    },

    /** provenance 칩의 清除 — 출고처 칩과 동일 관례: 값은 그대로 두고 "OCR 유래" 표시만 지운다 */
    clearOcrProvenance(key) {
      this.ocrProvenanceFields = this.ocrProvenanceFields.filter((k) => k !== key);
    },

    /** BE 에러 토큰(BLO-*) → 중문 메시지 + 원문 병기. 업로드 에러 매퍼와 동일 관례 */
    mapOcrError(err) {
      const status = err?.response?.status;
      const rawMessage = err?.response?.data?.message || '';

      if (!err?.response) {
        return this.$t('company_form_businessLicense_error_network');
      }
      if (status === 400) {
        // BLO-URL-001(허용 안 되는 URL) / IsIn 검증 실패(미지 provider) 모두 여기로 떨어진다
        return rawMessage
          ? `${this.$t('company_form_businessLicense_ocr_error_url')} — ${rawMessage}`
          : this.$t('company_form_businessLicense_ocr_error_url');
      }
      if (status === 403) {
        // denyMessage(BLO-AUTHZ-001)는 한국어 리터럴 — 중문 + 원문 병기로 통일
        return rawMessage
          ? `${this.$t('company_form_businessLicense_error_forbidden')} — ${rawMessage}`
          : this.$t('company_form_businessLicense_error_forbidden');
      }
      if (status === 429) {
        return this.$t('company_form_businessLicense_error_throttled');
      }
      if (status === 502) {
        const label = rawMessage.includes('BLO-EXTRACT-001')
          ? this.$t('company_form_businessLicense_ocr_error_extract_failed')
          : this.$t('company_form_businessLicense_ocr_error_failed');
        return rawMessage ? `${label} — ${rawMessage}` : label;
      }
      return rawMessage
        ? `${this.$t('company_form_businessLicense_ocr_error_generic')} — ${rawMessage}`
        : this.$t('company_form_businessLicense_ocr_error_generic');
    },
  },
};
